/**
 * photoQueue — durable offline upload queue for vehicle photos.
 *
 * KEY DIFFERENCE from serviceQueue: each item persists the RAW BINARY
 * (ArrayBuffer) in IndexedDB alongside the JSON metadata. This means an
 * offline-picked photo's bytes survive a full tab close / app reload —
 * the user can pick photos with no signal and the upload will drain the
 * next time they reconnect, even after restarting the app.
 *
 * serviceQueue by contrast stores only JSON (no binary). Photo uploads
 * are a 3-step protocol (upload-intent → PUT bytes → commit), so the
 * bytes must be available at drain time.
 *
 * Max 50 items. Older pending items are silently dropped when the cap is hit.
 * Cleared on sign-out to prevent cross-user leaks (§00).
 */

import { del, get, set } from 'idb-keyval';

const QUEUE_KEY = 'gg.photoQueue.v1';
export const MAX_PHOTO_QUEUE = 50;

// Change notification — subscribers are notified on every mutation so the UI
// re-reads immediately (same pattern as serviceQueue).
const listeners = new Set<() => void>();

/** Subscribe to queue mutations; returns an unsubscribe fn. */
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      // a subscriber throwing must not break a queue mutation
    }
  }
}

export type PhotoQueueStatus = 'pending' | 'inflight' | 'failed';

export interface PhotoQueueItem {
  /** crypto.randomUUID() — dedup key. */
  clientId: string;
  /** Owner uid stamped at enqueue time. Drain refuses items with mismatched uid. */
  uid: string;
  vehicleId: string;
  /** MIME type of the file. */
  contentType: string;
  /** File size in bytes. */
  sizeBytes: number;
  /** ISO timestamp derived from file.lastModified. */
  capturedAt: string;
  /** Optional caption captured at pick time. */
  caption?: string;
  /** ISO enqueue timestamp for optimistic ordering. */
  queuedAt: string;
  status: PhotoQueueStatus;
  /** photoId returned by upload-intent (available after step 1 succeeds). */
  photoId?: string;
  /** uploadUrl returned by upload-intent (available after step 1 succeeds). */
  uploadUrl?: string;
  /** LOCAL object URL for optimistic tile preview (URL.createObjectURL).
   * NOT persisted — only available in the same tab session. */
  previewUrl?: string;
  /** Upload progress 0–100, only meaningful while status === 'inflight'. */
  progress?: number;
  /**
   * Spec 011 — set when this photo was picked via a service-entry before/after
   * slot rather than the general album dropzone. Purely a client-side tag; the
   * server has no concept of "role" on a photo (before/after is a property of
   * the ServiceEntry's reference to the photo, not the photo itself).
   */
  role?: 'before' | 'after';
  /**
   * Spec 011 — correlates this queue item to the in-flight service entry it
   * was picked for (matches `QueuedEntry.clientMutationId` in serviceQueue.ts).
   * Set only when `role` is set. Read by the photo-drain in usePhotoQueue.ts
   * to write the resolved photoId back onto the matching service-queue entry
   * once upload+commit succeeds — see useServiceQueue.ts's pendingPhotoRefs
   * resolution for the other half of this handshake.
   */
  serviceEntryDraftId?: string;
}

/** Stored item: JSON metadata only — previewUrl and progress are runtime-only. */
export type StoredPhotoItem = Omit<PhotoQueueItem, 'previewUrl' | 'progress'>;

function bytesKey(clientId: string): string {
  return `gg.photoBytes.v1.${clientId}`;
}

async function readAll(): Promise<StoredPhotoItem[]> {
  return (await get<StoredPhotoItem[]>(QUEUE_KEY)) ?? [];
}

async function writeAll(items: StoredPhotoItem[]): Promise<void> {
  await set(QUEUE_KEY, items);
}

/** Append a new item with its binary payload. Drops oldest when cap is hit. */
export async function enqueue(item: StoredPhotoItem, bytes: ArrayBuffer): Promise<void> {
  const current = await readAll();
  const next = [...current, item];
  const capped = next.length > MAX_PHOTO_QUEUE ? next.slice(next.length - MAX_PHOTO_QUEUE) : next;

  // If items were dropped, clean up their binary blobs too.
  const kept = new Set(capped.map((i) => i.clientId));
  for (const prev of current) {
    if (!kept.has(prev.clientId)) {
      await del(bytesKey(prev.clientId));
    }
  }

  await set(bytesKey(item.clientId), bytes);
  await writeAll(capped);
  notify();
}

/** Retrieve the binary payload for a queue item. */
export async function getBytes(clientId: string): Promise<ArrayBuffer | undefined> {
  return get<ArrayBuffer>(bytesKey(clientId));
}

/** All items across all vehicles. */
export async function listAll(): Promise<StoredPhotoItem[]> {
  return readAll();
}

/** Items for a specific vehicle. */
export async function listFor(vehicleId: string): Promise<StoredPhotoItem[]> {
  const all = await readAll();
  return all.filter((q) => q.vehicleId === vehicleId);
}

/**
 * Patch a queued item by clientId. Pass `photoId: undefined` / `uploadUrl: undefined`
 * to clear a stale upload-intent (e.g. once the signed URL expires) so the next
 * attempt re-requests a fresh one instead of reusing a dead URL.
 *
 * Spec 011 — `role` is patchable so Swap can re-tag a still-queued (not yet
 * uploaded) before/after photo; the eventual write-back in usePhotoQueue.ts's
 * drain reads whatever `role` is current at upload time, so a swap must
 * update the persisted item, not just the slots UI's local state.
 */
export async function update(
  clientId: string,
  patch: Partial<Pick<StoredPhotoItem, 'status' | 'role'>> & {
    photoId?: string | undefined;
    uploadUrl?: string | undefined;
  },
): Promise<void> {
  const all = await readAll();
  await writeAll(
    all.map((q) => {
      if (q.clientId !== clientId) return q;
      const next = { ...q, ...patch };
      if ('photoId' in patch && patch.photoId === undefined) delete next.photoId;
      if ('uploadUrl' in patch && patch.uploadUrl === undefined) delete next.uploadUrl;
      return next as StoredPhotoItem;
    }),
  );
  notify();
}

/** Remove a queued item and its binary payload. */
export async function remove(clientId: string): Promise<void> {
  const all = await readAll();
  await writeAll(all.filter((q) => q.clientId !== clientId));
  await del(bytesKey(clientId));
  notify();
}

/**
 * Clear the entire queue and all binary payloads.
 * Called on sign-out to prevent User A's unsynced photos from uploading under
 * User B on a shared device (§00 SOC2 BLOCKER).
 */
export async function clearAll(): Promise<void> {
  const all = await readAll();
  for (const item of all) {
    await del(bytesKey(item.clientId));
  }
  await set(QUEUE_KEY, []);
  notify();
}
